import {
  Component,
  OnInit,
  HostListener,
  inject,
  signal,
  DestroyRef,
} from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../authentication/auth.service';
import { AccountDataService } from '../../../site-pages/secure-pages/account-data.service';
import { isEqual } from 'lodash';
import { TreeModule } from '@ali-hm/angular-tree-component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoadingService } from '../../../shared/services/loading.service';
import { ProblemSavingDialogComponent } from './problem-saving-dialog/problem-saving-dialog.component';
import { MatDialog } from '@angular/material/dialog';

import { AccountSettingsDialogComponent } from './account-settings-dialog/account-settings-dialog.component';
import { ToolBarComponent } from '../tool-bar/tool-bar.component';
import { MaterialModule } from '../../../modules/material.module';
import { catchError, map, Observable, throwError } from 'rxjs';
import { HttpQueueService } from '../../../shared/services/http-queue.service';
import { AlertDialogComponent } from '../../../shared/components/alert-dialog/alert-dialog.component';
import {
  DASHBOARD_THEMES,
  DEFAULT_ITEM_VALUE,
  HISTORY_ACTIONS,
  HISTORY_BASIC_PROPERTIES,
  HISTORY_OBJECT_TYPES,
  THEME_NAMES,
} from './utils/constant';
import { TreeItemService } from '../../../shared/services/tree-item.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { joinWithQuotes, sortByKey } from '../../../shared/utils/common';
import { ProcessQueueRequestAction, SaveActionType, TreeEventType } from '../../../shared/utils/enums';
import { AccountService } from '../../../shared/services/account.service';
import { AccountSettings, Attachment, NodeItem, Subscription } from '../../../shared/utils/types';
import moment from 'moment';

@Component({
  selector: 'app-main-dashboard',
  templateUrl: './main-dashboard.component.html',
  styleUrls: ['./main-dashboard.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TreeModule,
    ToolBarComponent,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    RouterModule,
  ],
  providers: [AccountDataService],
})
export class MainDashboardComponent implements OnInit {
  private ac = inject(AccountDataService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private readonly loadingService = inject(LoadingService);
  private readonly dialog = inject(MatDialog);
  private readonly httpQueueService = inject(HttpQueueService);
  protected treeItemService = inject(TreeItemService);
  protected accountService = inject(AccountService);

  // variables related to user items
  private itemBeingEdited: NodeItem; // dictionary containing data of item currently displayed in "edit item" dialog box (if any)

  // variables related to syncing with the backend
  private windowFocused = signal<boolean>(true); // whether the browser window is focused (item dashboard periodically refreshes so long as this is the case)
  private timeOfLastItemUpdate = signal<Date>(null); // datetime stamp of the most recent user update to an item (add/delete/edit/reposition/moveFromTrash etc.)
  private timeOfLastRefresh = signal<Date>(null); // datetime stamp of the last time the dashboard was refreshed
  private unsavedChanges: Object[] = []; // array of dictionaries representing each new not-yet-saved change the user has made to their items

  // variables related to general user account settings
  private newlyAccessedAccount: boolean; // whether this is the first time the user is accessing their account
  THEME_NAMES = THEME_NAMES;
  // whether the nav bar save button is currently displayed in the dashboard
  navBarSaveButton: {
    disabled: boolean;
    text: string;
  } = {
      disabled: true,
      text: 'Saved',
    };

  showWelcomeDialog = signal(false);
  private destroyRef = inject(DestroyRef);

  // trigger an "unsaved changes" alert when user attempts to navigate away from the browser window with unsaved changes
  @HostListener('window:beforeunload', ['$event'])
  confirmExit($event) {
    if (this.unsavedChanges.length > 0) {
      $event.returnValue = true;
    }
  }

  ngOnInit(): void {
    this.loadingService.show();
    this.processQueueRequests();

    this.itemBeingEdited = DEFAULT_ITEM_VALUE;

    const self = this;
    const firebaseUser = this.auth.getCurrentUser();
    firebaseUser.getIdToken().then(function (token) {
      sessionStorage.setItem('token', token);
      self.loadData();
    });

    this.treeItemService.treeEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const { type, data: info } = data;
        switch (type) {
          case TreeEventType.SaveItem:
            this.itemBeingEdited = info;
            this.saveItemButtonAction();
            break;

          case TreeEventType.MoveFromTrash:
            self.moveItemActionTrash(info, false);
            break;

          case TreeEventType.MoveToTrash:
            this.moveItemToTrashButtonAction(info);
            break;

          case TreeEventType.OnMovedNode:
            this.onMovedNode(info);
            break;

          default:
            break;
        }
      });
  }

  private loadData() {
    // refresh dashboard by fetching account data from the backend
    this.refreshDashboard().subscribe({
      next: () => {
        if (
          this.newlyAccessedAccount ||
          (this.auth.justLoggedIn && this.accountService.otherAccountSettings().optOutWelcomePopup == false)
        ) {
          this.showWelcomeDialog.set(true);
          this.auth.justLoggedIn = false;
        }

        // on browser window focus, dashboard auto refresh periodically until its focused
        let autoRefresh;
        window.addEventListener('focus', () => {
          if (!this.windowFocused()) {
            const timeInterval = 60;
            if (!this.httpQueueService.hasRequest(ProcessQueueRequestAction.RefreshDashboard)) {
              this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.RefreshDashboard);
            }
            autoRefresh = setInterval(() => {
              // calculate seconds since last refresh
              let secondsSinceLastRefresh = null;
              if (this.timeOfLastRefresh()) {
                secondsSinceLastRefresh =
                  (new Date().getTime() - this.timeOfLastRefresh().getTime()) /
                  1000;
              }

              // if it's been a while since the last dashboard refresh, queue a dashboard refresh if it isn't queued already
              if (
                !secondsSinceLastRefresh ||
                secondsSinceLastRefresh > timeInterval
              ) {
                if (!this.httpQueueService.hasRequest(ProcessQueueRequestAction.RefreshDashboard)) {
                  this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.RefreshDashboard);
                }
              }
            }, timeInterval * 1000);
            this.windowFocused.set(true);
          }
        });
        window.addEventListener('blur', () => {
          if (this.windowFocused()) {
            clearInterval(autoRefresh);
            autoRefresh = 0;
            this.windowFocused.set(false);
          }
        });

        this.loadingService.hide();
      },
      error: () => {
        // if request for newest account data unsuccessful, wait 10 seconds before trying again
        setTimeout(() => {
          this.ngOnInit();
        }, 10000);
      },
    });
  }

  /*
   * handle request for welcome help dialog when close from the toolbar
   */
  handleAfterCloseWelcomeDialog() {
    this.showWelcomeDialog.set(false);
  }

  /**
   * if there are any unsaved changes, wait until there's been a pause since the last item update and then save any unsaved changes
   */
  private scheduleAutoSave(): void {
    if (this.unsavedChanges.length > 0) {
      const timeInterval = 10;

      // find seconds since last item update
      let secondsSinceLastItemUpdate;
      if (this.timeOfLastItemUpdate()) {
        secondsSinceLastItemUpdate =
          (new Date().getTime() - this.timeOfLastItemUpdate().getTime()) / 1000;
      }

      if (
        this.timeOfLastItemUpdate() == null ||
        secondsSinceLastItemUpdate > timeInterval
      ) {
        // if it's been a while since the last item update
        if (!this.httpQueueService.hasRequest(ProcessQueueRequestAction.SaveNewChanges)) {
          this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.SaveNewChanges);
        }
      } else {
        setTimeout(() => {
          this.scheduleAutoSave();
        }, 1000 * timeInterval);
      }
    }
  }

  /**
   * an update to an item, push it to the "unsavedChanges" array
   */
  private queueItemUpdate(itemUpdate: any): void {
    const itemUpdateCopy = structuredClone(itemUpdate);
    if (itemUpdateCopy.type == SaveActionType.AddItem) {
      this.accountService.subscription.update((state) => {
        state.numItemsAddedThisMonth += 1;
        return state;
      });
    }
    this.unsavedChanges.push(itemUpdateCopy);

    if (itemUpdate.data.itemId) {
      const thisNode = this.treeItemService.getItemById(itemUpdate.data.itemId);
      if (thisNode) {
        thisNode.history = thisNode.history?.map((item) => ({
          ...item,
          new: false,
        }));
        this.treeItemService.allTreeNodeItems.update((state) => {
          return state.map((item) =>
            item.id === thisNode.id ? { ...thisNode } : item
          );
        });
      }
    }
    this.saveBtnSavedDisplay('Save', false);
  }

  /**
   * Watch on queue and process the request
   */
  private processQueueRequests(): void {
    this.httpQueueService.watch().subscribe({
      next: (request) => {
        switch (request) {
          case ProcessQueueRequestAction.RefreshDashboard:
            // if there are new unsaved item changes, first save changes
            if (this.unsavedChanges.length > 0) {
              if (!this.httpQueueService.hasRequest(ProcessQueueRequestAction.SaveNewChanges)) {
                this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.SaveNewChanges);
              }
              if (!this.httpQueueService.hasRequest(ProcessQueueRequestAction.RefreshDashboard)) {
                this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.RefreshDashboard);
              }
              this.httpQueueService.requestCompleted();
            }
            // else refresh the dashboard and keep cycling through requests
            else {
              this.refreshDashboard().subscribe({
                next: () => {
                  this.httpQueueService.requestCompleted();
                },
                error: () => {
                  // if error in refreshing, wait then try again
                  this.httpQueueService.requestFailed();
                },
              });
            }
            break;
          case ProcessQueueRequestAction.SaveNewChanges:
            // save new changes if any, keep cycling through requests
            const unsavedChangesCopy = structuredClone(this.unsavedChanges);
            if (unsavedChangesCopy.length == 0) {
              this.saveBtnSavedDisplay('Saved', true);
              this.httpQueueService.requestCompleted();
            } else {
              this.saveUnsavedChanges(unsavedChangesCopy).subscribe({
                next: () => {
                  this.unsavedChanges.splice(0, unsavedChangesCopy.length); // remove just saved changes from "unsavedChanges" variable
                  this.saveBtnSavedDisplay('Saved', true);
                  this.httpQueueService.requestCompleted();
                },
                error: () => {
                  // if error in saving, wait then try again
                  this.httpQueueService.requestFailed();
                },
              });
            }
            break;
          case ProcessQueueRequestAction.SaveWelcomePopupPreference:
            this.ac
              .updateWelcomePopupPreference(this.accountService.otherAccountSettings().optOutWelcomePopup)
              .subscribe({
                next: () => {
                  if (environment.debuggingConsoleLogsOn) {
                    console.log('Saved new intro tutorial opt out preference.');
                  }
                  this.httpQueueService.requestCompleted();
                },
                error: () => {
                  this.httpQueueService.requestCompleted();
                },
              });
            break;
          case ProcessQueueRequestAction.SaveDashboardTheme:
            this.ac
              .saveDashboardTheme(this.accountService.otherAccountSettings().dashboardTheme.name)
              .subscribe({
                next: (response) => {
                  if (environment.debuggingConsoleLogsOn) {
                    console.log(response['result']);
                  }
                  this.httpQueueService.requestCompleted();
                },
                error: () => {
                  this.httpQueueService.requestCompleted();
                },
              });
            break;
        }
      },
    });
  }

  /**
   * given an array of dictionaries representing a set of unsaved changes returns a promise to save these to the backend
   */
  private saveUnsavedChanges(unsavedChanges: any): Observable<any> {
    if (environment.debuggingConsoleLogsOn) {
      console.log('Saving newest changes...');
    }
    this.saveBtnSavedDisplay('Saving', true);

    return this.ac.saveUnsavedChanges(unsavedChanges).pipe(
      map((response) => {
        if (environment.debuggingConsoleLogsOn) {
          console.log(response.result);
        }
        return response;
      }),
      catchError((error) => {
        if (environment.debuggingConsoleLogsOn) {
          console.log('Error in saving latest changes.');
        }
        this.dialog.open(ProblemSavingDialogComponent);
        return throwError(() => new Error(error));
      })
    );
  }

  /**
   * actions taken when a user clicks the nav bar save button at the top right in the dashboard
   */
  navBarSaveButtonAction(): void {
    this.saveBtnSavedDisplay('Saving', true);

    if (!this.httpQueueService.hasRequest(ProcessQueueRequestAction.SaveNewChanges)) {
      this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.SaveNewChanges);
    }
  }

  /**
   * function for adding data in history data
   */
  private prepareHistoryData(
    action: string,
    objType: string,
    objName: string,
    beforeValue: string,
    afterValue: string
  ) {
    const newEvent = {
      action: action,
      object_type: objType,
      object_name: this.abbreviatedVersion(objName),
      timestamp: new Date(),
      before_value: this.abbreviatedVersion(beforeValue),
      after_value: this.abbreviatedVersion(afterValue),
      user: this.auth.getCurrentUser().email,
      was_automated_action: false,
      new: true,
    };
    this.itemBeingEdited['history'].unshift(newEvent);
  }

  /**
   * queues the item being edited to be saved to the backend database
   */
  private saveItemButtonAction(): void {
    this.timeOfLastItemUpdate.set(new Date());
    const isNewItem = this.itemBeingEdited.new;

    // make lowercase, convert spaces to underscores in any item tags
    const newTags = this.itemBeingEdited.tags
      ?.map((tag) => tag?.trim()?.replace(' ', '_')?.toLowerCase())
      .filter((tag) => tag?.length);
    this.itemBeingEdited.tags = Array.from(new Set(newTags));

    // trim, filter out invalid share permissions
    this.itemBeingEdited.shared_with = this.itemBeingEdited.shared_with?.filter(
      (permissions) => {
        const email = permissions?.user?.email?.trim();
        if (email.indexOf('@') > 0 && email.indexOf('@') != email.length - 1) {
          return email;
        }
      }
    );

    // add default title if empty
    if (!this.itemBeingEdited.title) {
      const itemType = this.itemBeingEdited.type;
      this.itemBeingEdited.title = !itemType
        ? 'Untitled'
        : `Untitled ${itemType}`;
    }

    if (isNewItem) {
      this.handleNewItemSave();
    } else {
      this.updateExistingItemSave();
    }

    // update item tree
    const itemBeingEditedCopy = structuredClone(this.itemBeingEdited);
  
    if (isNewItem) {
      this.treeItemService.allTreeNodeItems.update((state) => {
        return [itemBeingEditedCopy, ...state];
      });
      if (itemBeingEditedCopy.parent_id) {
        const expandParentId = itemBeingEditedCopy.parent_id;
        // expand parent node
        this.treeItemService.setTreeEvent({
          type: TreeEventType.ExpandNode,
          data: expandParentId,
        });
      }
    } else {
      this.treeItemService.allTreeNodeItems.update((state) => {
        return state.map((item) => (item.id === itemBeingEditedCopy.id ? { ...itemBeingEditedCopy } : item));
      });
    }

    this.updateInheritedPermissionsForItemAndDescendants(itemBeingEditedCopy);

    // add action to array of newest changes
    const itemSave = {
      type: isNewItem ? SaveActionType.AddItem : SaveActionType.UpdateItem,
      timestamp: new Date(),
      data: { itemId: this.itemBeingEdited.id, item: this.itemBeingEdited },
    };
    this.queueItemUpdate(itemSave);
    this.scheduleAutoSave();
  }

  /**
   * Handle the save operation for a new item
   * Set display order and initialize history
   */
  private handleNewItemSave(): void {
    const parentId = this.itemBeingEdited.parent_id;
    let nodeItems = this.treeItemService.allTreeNodeItems().filter(item => !item.in_trash); //TODO : need to change logic for display_order
    if (parentId) {
      nodeItems = this.treeItemService.getChildren(parentId);
    }
    nodeItems = sortByKey(nodeItems, 'display_order');

    // set default display order
    this.itemBeingEdited.display_order = nodeItems?.length
      ? nodeItems[0].display_order - 1
      : 0;

    // initialize item history with creation record
    this.itemBeingEdited.history = [
      {
        action: HISTORY_ACTIONS.ADD,
        object_type: HISTORY_OBJECT_TYPES.ITEM,
        object_name: this.abbreviatedVersion(this.itemBeingEdited.title),
        timestamp: new Date(),
        before_value: null,
        after_value: null,
        user: this.auth.getCurrentUser().email,
        was_automated_action: false,
        new: true,
      },
    ];
    
    this.itemBeingEdited.new = false;
  }

  /**
   * Update the save operation for an existing item
   */
  private updateExistingItemSave(): void {
    const originalItem: NodeItem = this.treeItemService.getItemById(this.itemBeingEdited.id);
    
    // Update history based on changes
    this.updateBasicPropertyHistories(originalItem);
    this.updateTagsHistory(originalItem);
    this.updateAttachmentsHistory(originalItem);
    this.updateSharePermissionsHistory(originalItem);
    this.updateAssigneesHistory(originalItem);
  }

  /**
   * Update history for basic properties like title, type, status, etc.
   */
  private updateBasicPropertyHistories(originalItem: NodeItem): void {
    Object.keys(HISTORY_BASIC_PROPERTIES).forEach((property) => {
      if (this.itemBeingEdited[property] !== originalItem[property]) {
        const propertyName = HISTORY_BASIC_PROPERTIES[property];
        let beforeValue = originalItem[property];
        let afterValue = this.itemBeingEdited[property];

        if (property === 'start_date' || property === 'due_date') {
          beforeValue = beforeValue ? moment(beforeValue).format("dddd, MMMM D, YYYY") : 'empty';
          afterValue = afterValue ? moment(afterValue).format("dddd, MMMM D, YYYY") : 'empty';
        } else {
          // beforeValue and afterValue may be numbers, so we need to convert them to strings.
          beforeValue = beforeValue ? `${beforeValue}` : 'empty';
          afterValue = afterValue ? `${afterValue}` : 'empty';
        }

        this.prepareHistoryData(
          HISTORY_ACTIONS.UPDATE,
          propertyName,
          beforeValue,
          beforeValue,
          afterValue
        );
      }
    });
  }

  /**
   * Update history for tags changes
   */
  private updateTagsHistory(originalItem: NodeItem): void {
    const oldTags = structuredClone(originalItem.tags);
    const oldTagCounts = this.getCounts(oldTags);
    const newTags = structuredClone(this.itemBeingEdited.tags);
    const newTagCounts = this.getCounts(newTags);
    
    if (!isEqual(oldTagCounts, newTagCounts)) {
      this.prepareHistoryData(
        HISTORY_ACTIONS.UPDATE,
        HISTORY_OBJECT_TYPES.TAGS,
        this.addHashTags(oldTags).join(', '),
        this.addHashTags(oldTags).join(', '),
        this.addHashTags(newTags).join(', ')
      );
    }
  }

  /**
   * Update history for attachment changes
   */
  private updateAttachmentsHistory(originalItem: NodeItem): void {
    const oldAttachments = originalItem?.attachments?.map((att: Attachment) => att.name);
    const newAttachments = this.itemBeingEdited?.attachments?.map((att: Attachment) => att.name);

    const removedAttachments = oldAttachments?.filter(
      (att) => !newAttachments.includes(att)
    );
    const addedAttachments = newAttachments?.filter(
      (att) => !oldAttachments.includes(att)
    );

    if (removedAttachments?.length > 0) {
      const displayText = joinWithQuotes(removedAttachments, ' and ');
      this.prepareHistoryData(
        HISTORY_ACTIONS.REMOVE,
        removedAttachments.length > 1 ? HISTORY_OBJECT_TYPES.ATTACHMENTS : HISTORY_OBJECT_TYPES.ATTACHMENT,
        displayText,
        null,
        displayText
      );
    }

    if (addedAttachments?.length > 0) {
      const displayText = joinWithQuotes(addedAttachments, ' and ');
      this.prepareHistoryData(
        HISTORY_ACTIONS.ADD,
        addedAttachments.length > 1 ? HISTORY_OBJECT_TYPES.ATTACHMENTS : HISTORY_OBJECT_TYPES.ATTACHMENT,
        displayText,
        null,
        displayText
      );
    }
  }

  /**
   * Update history for share permissions changes
   */
  private updateSharePermissionsHistory(originalItem: NodeItem): void {
    const oldUsersSharedWith =
      originalItem.shared_with?.map((share) => share.user.email) || [];
    const newUsersSharedWith =
      this.itemBeingEdited.shared_with?.map((share) => share.user.email) || [];

    const usersRemoved = oldUsersSharedWith.filter(
      (user) => !newUsersSharedWith.includes(user)
    );
    const usersAdded = newUsersSharedWith.filter(
      (user) => !oldUsersSharedWith.includes(user)
    );

    if (usersRemoved.length > 0) {
      const displayText = usersRemoved.join(', ');
      this.prepareHistoryData(
        HISTORY_ACTIONS.REMOVE,
        HISTORY_OBJECT_TYPES.SHARE_PERMISSION,
        displayText,
        null,
        displayText
      );
    }
    
    if (usersAdded.length > 0) {
      const displayText = usersAdded.join(', ');
      this.prepareHistoryData(
        HISTORY_ACTIONS.ADD,
        HISTORY_OBJECT_TYPES.SHARE_PERMISSION,
        displayText,
        null,
        displayText
      );
    }
  }

  /**
   * Update history for assignee changes
   */
  private updateAssigneesHistory(originalItem: NodeItem): void {
    const oldAssignees = originalItem.assignees;
    const newAssignees = this.itemBeingEdited.assignees;

    const removedAssignees = oldAssignees?.filter(
      (user) => !newAssignees.includes(user)
    );
    const addedAssignees = newAssignees.filter(
      (user) => !oldAssignees.includes(user)
    );

    if (removedAssignees.length > 0) {
      const displayText = removedAssignees.join(', ');
      this.prepareHistoryData(
        HISTORY_ACTIONS.REMOVE,
        HISTORY_OBJECT_TYPES.ASSIGNEE,
        displayText,
        null,
        displayText
      );
    }

    if (addedAssignees.length > 0) {
      const displayText = addedAssignees.join(', ');
      this.prepareHistoryData(
        HISTORY_ACTIONS.ADD,
        HISTORY_OBJECT_TYPES.ASSIGNEE,
        displayText,
        null,
        displayText
      );
    }
  }

  /**
   * display confirmation for removing item and on remove add item to trash
   */
  private moveItemToTrashButtonAction(id: string): void {
    this.timeOfLastItemUpdate.set(new Date());
    const alertMessage =
      'This action will move this item and all of its descendents to trash. To view items in trash, select the trash icon in the navigation bar.';

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: alertMessage,
        primaryButtonText: 'Ok',
        secondaryButtonText: 'Cancel',
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.moveItemActionTrash(id, true);
      }
    });
  }

  private moveItemActionTrash(itemId: string, isMovingToTrash: boolean): void {
    const itemToMove = this.treeItemService.getItemById(itemId);
    const currentUserEmail = this.auth.getCurrentUser().email;
    const timestamp = new Date();

    // Update numItemsAddedThisMonth based on the action
    this.accountService.subscription.update((state) => {
      state.numItemsAddedThisMonth += isMovingToTrash ? -1 : 1;
      return state;
    });

    if (itemToMove) {
      const action = isMovingToTrash ? 'remove' : 'undelete';

      // Create history record for trash action
      const newEvent = {
        action: action,
        object_type: HISTORY_OBJECT_TYPES.ITEM,
        object_name: this.abbreviatedVersion(itemToMove.title),
        timestamp: timestamp,
        before_value: null,
        after_value: null,
        user: currentUserEmail,
        was_automated_action: false,
        new: true,
      };
      itemToMove.history.unshift(newEvent);

      // Move to trash item display as top of items 
      let trashItems = this.treeItemService.allTreeNodeItems();
      //TODO : need to change logic for display_order
      trashItems = sortByKey(trashItems, 'display_order');
      if (trashItems.length) {
        itemToMove.display_order = trashItems[0].display_order - Math.random();
      }

      // Update item and its descendants 'in_trash' property
      this.updateInTrashPropertyForItemAndDescendants(itemToMove, isMovingToTrash);

      // Update the history of the item
      this.treeItemService.allTreeNodeItems.update((state) => {
        return state.map((item) =>
          item.id === itemToMove.id ? { ...itemToMove } : item
        );
      });

      // Add action to array of newest changes
      const itemUpdate = {
        type: isMovingToTrash ? SaveActionType.MoveItemToTrash : SaveActionType.MoveItemFromTrash,
        timestamp: timestamp,
        data: { itemId: itemToMove.id, item: itemToMove },
      };
      this.queueItemUpdate(itemUpdate);
    }
    this.scheduleAutoSave();
  }

  /**
   * update dashboard with newest account data after retrieving it from the backend
   */
  private refreshDashboard(): Observable<any> {
    if (environment.debuggingConsoleLogsOn) {
      console.log('Refreshing dashboard...');
    }

    return this.ac.getNewestAccountData().pipe(
      map((data) => {
        // if there are no new item updates queued to be saved, update dashboard data with newest values from the backend
        if (
          this.unsavedChanges.length == 0 &&
          !this.httpQueueService.hasRequest(ProcessQueueRequestAction.SaveDashboardTheme) &&
          !this.httpQueueService.hasRequest(ProcessQueueRequestAction.SaveWelcomePopupPreference)
        ) {
          this.treeItemService.allTreeNodeItems.set(data.items);

          // set subscription related data
          const subscription: Subscription = {
            isProAccount: data.pro_account_info.is_pro_account,
            maxNumItemsAdd: data.pro_account_info.non_pro_max_added_items,
            numItemsAddedThisMonth: data.pro_account_info.net_num_items_added_this_month,
            proExpiryDate: data.pro_account_info.pro_expiry_date,
            credCardLastFourDigits: data.pro_account_info.cred_card_last_four_digits,
          }
          this.accountService.subscription.set(subscription);
          // Other Account settings 
          const accountSettings: AccountSettings = {
            maxLengthLongText: data.other_settings.max_len_long_txt,
            maxLengthShortText: data.other_settings.MAX_LEN_SHORT_TEX,
            optOutWelcomePopup: data.other_settings.opted_out_welcome_popup,
            dashboardTheme: DASHBOARD_THEMES.find((theme) => theme.name === data.other_settings.dashboard_theme)
          }
          if (!subscription.isProAccount && subscription.proExpiryDate) {
            // If user's pro account is expired then reset the theme dashboard to the default
            accountSettings.dashboardTheme = DASHBOARD_THEMES.find(theme => theme.name === THEME_NAMES.DEFAULT);
          }
          this.accountService.otherAccountSettings.set(accountSettings);

          // set other component properties
          this.newlyAccessedAccount = data.is_new_user;

          this.timeOfLastRefresh.set(new Date());

          if (environment.debuggingConsoleLogsOn) {
            console.log(
              'Dashboard refreshed. Item tree:',
              this.treeItemService.allTreeNodeItems()
            );
          }
        }
        return data;
      }),
      catchError((error) => {
        if (environment.debuggingConsoleLogsOn) {
          console.log('Error in fetching newest item data. Details:', error);
        }
        return throwError(() => new Error(error));
      })
    );
  }

  /**
   * given a tree node, returns an array of those share permissions that the node has inherited from its ancestors that are not explicitly assigned to the node
   */
  private getInheritedPermissions(node: any): any[] {
    const permission = node
      ? node['shared_with'].filter((p) => p.type !== 'Explicit')
      : [];
    return permission;
  }

  /**
   * given a tree node, returns an array of those share permissions that the node has inherited from its ancestors that are not explicitly assigned to the node
   */
  private getExplicitPermissions(node: any): any[] {
    const permission = node
      ? node['shared_with'].filter((p) => p.type == 'Explicit')
      : [];
    return permission;
  }

  /**
   * concatenate string with an ellipses ("...") if the string is long
   */
  private abbreviatedVersion(str: string): string {
    const snippetLength = 50;
    return str && str.length > snippetLength
      ? str.substring(0, snippetLength) + '...'
      : str || '';
  }


  /**
   * deactivate and blur any focused items (tree nodes) if click away from item
   */
  setFocus($event: any): void {
    const clickedOnItem = this.containsItemElem($event.path);
    if (!clickedOnItem) {
      this.blurFocusedNodes();
    }
  }

  /**
   * deactivate and blur any activated or focused nodes
   */
  private blurFocusedNodes(): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.BlurFocusedNode,
      data: '',
    });
  }

  /**
   * given a set of html elements, return whether any of them are items in the item tree
   */
  private containsItemElem(elems: any[]): boolean {
    if (!elems) return false;
    const itemElems = elems.filter(
      (e) => e.className?.split(' ').indexOf('item') > -1
    );

    return itemElems.length > 0;
  }

  /**
   * concat # character to each tag in an array of tags
   */
  private addHashTags(arrayOfTags: string[]): string[] {
    return arrayOfTags.map((tag) => `#${tag}`);
  }

  /**
   * modify nav bar save button to reflect that there are new changes currently being saved, saving or to save
   */
  private saveBtnSavedDisplay(text: string, disabled: boolean): void {
    this.navBarSaveButton.disabled = disabled;
    this.navBarSaveButton.text = text;
  }

  /**
   * storing the number of times each distinct strings appears in the array; e.g., ['tag1','tag2','tag2','tag2'] -> {'tag1': 1, 'tag2': 3}
   */
  private getCounts(strArray: string[]): Object {
    const counts = {};
    for (const item of strArray) {
      counts[item] = !counts[item] ? 1 : counts[item] + 1;
    }
    return counts;
  }

  logout(): void {
    const self = this;

    this.loadingService.show();

    // wait until no further unsaved changes before logging out
    if (this.unsavedChanges.length > 0) {
      setTimeout(() => {
        self.logout();
      }, 3000);
    } else {
      this.auth.logout().then((data) => {
        this.loadingService.hide();
        this.router.navigate(['login']);
      });
    }
  }

  /**
   * display a modal dialog for delete account
   */
  showAccountSettingsDialog(): void {
    this.dialog.open(AccountSettingsDialogComponent);
  }

  applySearchFilter(text: string = ''): void {
    this.treeItemService.searchedText.set(text);
  }

  /**
   * given a tree node, update its inherited share permissions as well as those for its descendants
   */
  private updateInheritedPermissionsForItemAndDescendants(node: any): void {
    const existingInheritedPermissions = this.getInheritedPermissions(node);
    const existingExplicitPermissions = this.getExplicitPermissions(node);
    let permissionsToInherit = [];
    if (node.parent_id) {
      const parentNode = this.treeItemService.getItemById(node.parent_id);
      permissionsToInherit = parentNode.shared_with;
    }

    // remove any permissions that should no longer be inherited
    for (const permission of existingInheritedPermissions) {
      const y = permissionsToInherit.filter(
        (e) => e.user.email == permission.user.email
      );
      if (!y.length) {
        permission.toBeRemoved = true;
      }
    }
    node.shared_with = existingInheritedPermissions
      .filter((p) => !p.toBeRemoved)
      .concat(existingExplicitPermissions);

    // add any that should be inherited if not already there
    for (const permission of permissionsToInherit) {
      const y = node.shared_with.filter(
        (e) => e.user.email == permission.user.email
      );
      if (!y.length) {
        node.shared_with.push({
          user: { email: permission.user.email },
          type: 'Inherited',
        });
      }
    }

    // add current user permission back in if missing
    const self = this;
    const currentUserStillInSharedWith =
      node.shared_with.filter(
        (e) => e.user.email == self.auth.getCurrentUser().email
      )?.length > 0;
    if (!currentUserStillInSharedWith) {
      const currentUserPermission = {
        type: 'Explicit',
        user: {
          display_name: this.auth.getCurrentUser().displayName,
          firebase_uid: this.auth.getCurrentUser().uid,
          email: this.auth.getCurrentUser().email,
        },
      };
      node.shared_with.push(currentUserPermission);
    }

    if (node) {
      this.treeItemService.allTreeNodeItems.update((state) => {
        return state.map((item) => (item.id === node.id ? { ...node } : item));
      });
    }

    // do the same for its descendents
    const children = this.treeItemService.getChildren(node.id);
    if (children?.length) {
      for (const child of children) {
        this.updateInheritedPermissionsForItemAndDescendants(child);
      }
    }
  }

  /**
   * given a node, make all of its inherited permissions to be instead explicit and do the same for its descendents
   */
  private makeAllInheritedPermissionsExplicit(node: any): void {
    if (node) {
      node.shared_with = node.shared_with.map((perm) => ({
        ...perm,
        type: 'Explicit',
      }));

      this.treeItemService.allTreeNodeItems.update((state) => {
        return state.map((item) => (item.id === node.id ? { ...node } : item));
      });

      // do the same for this node's descendents
      const children = this.treeItemService.getChildren(node.id);
      if (children.length) {
        for (const child of children) {
          this.makeAllInheritedPermissionsExplicit(child);
        }
      }
    }
  }

  /**
   * when the user drags and drops an item to a different position within the tree - update the item's corresponding parent_id and display_order attributes
   */
  onMovedNode($event: any): void {
    this.timeOfLastItemUpdate.set(new Date());

    const itemMovedId = $event.node.id;
    const itemMoved = this.treeItemService.getItemById(itemMovedId);

    const newParent = $event.to.parent;
    const newParentId = newParent.virtual ? null : newParent.id;

    const newIndex = $event.to.index;

    const newOlderSib = newParent.children[newIndex - 1];
    const DisplayOrderOS = newOlderSib ? newOlderSib.display_order : null;

    const newYoungerSib = newParent.children[newIndex + 1];
    const DisplayOrderYS = newYoungerSib ? newYoungerSib.display_order : null;

    let newDisplayOrder: number = 0;
    const randomFraction = Math.random();
    if (newOlderSib && newYoungerSib) {
      // if item now has both a new older and a new younger sibling
      newDisplayOrder =
        DisplayOrderOS + randomFraction * (DisplayOrderYS - DisplayOrderOS);
    } else if (newOlderSib) {
      // if item now has only an older sibling
      newDisplayOrder = DisplayOrderOS + randomFraction * 10;
    } else if (newYoungerSib) {
      // if item now has only a younger sibling
      newDisplayOrder = DisplayOrderYS - randomFraction * 10;
    }
    if (itemMoved) {
      itemMoved['parent_id'] = newParentId;
      itemMoved['display_order'] = newDisplayOrder;

      // given a tree node, update its inherited share permissions as well as those for its descendants
      this.updateInheritedPermissionsForItemAndDescendants(itemMoved);

      this.treeItemService.allTreeNodeItems.update((state) => {
        return state.map((item) => (item.id === itemMoved.id ? { ...itemMoved } : item));
      });

      // add action to array of newest changes
      const itemUpdate = {
        type: SaveActionType.UpdateItem,
        timestamp: new Date(),
        data: { itemId: itemMovedId, item: itemMoved },
      };
      this.queueItemUpdate(itemUpdate);
    }
    this.scheduleAutoSave();
  }

  /**
   * update item and its descendants 'in_trash' property
   */
  private updateInTrashPropertyForItemAndDescendants(
    item: any,
    newValue: boolean
  ): void {
    const node = this.treeItemService.getItemById(item.id);
    if (node) {
      node.in_trash = newValue;
      const children = this.treeItemService.getChildren(item.id);
      for (const child of children) {
        this.updateInTrashPropertyForItemAndDescendants(child, newValue);
      }
    }
  }
}
